import { AiBookIcon, Calendar03Icon, UserMultiple02Icon } from '@hugeicons/core-free-icons';

/**
 * The Event Database: every collection an event has, by kind (D39).
 * - Knowledge base: the pages, files and text agents answer from (production's collections v2).
 * - Attendee collection: the people (delegates and sponsors), one row each, keyed by their CRM contact ID (D6).
 * - Event collection: the programme, one row per session.
 * The kind is `collectionData.collectionType`. Attendee and event collections hold rows ("records") with the
 * fields below, under each row's `fields`; a row missing a required field is `incomplete`, lists them in
 * `missingFields`, and is left out of matching until it's complete. The field keys are the API's.
 */
export const COLLECTION_KINDS = Object.freeze({
  knowledgeBase: 'knowledgeAgent',
  attendees: 'attendeeCollection',
  sessions: 'eventCollection',
});

export const COLLECTION_KIND_OPTIONS = [
  {
    value: COLLECTION_KINDS.knowledgeBase,
    title: 'Knowledge base',
    description: 'Pages, files and text your agents answer from.',
    icon: AiBookIcon,
  },
  {
    value: COLLECTION_KINDS.attendees,
    title: 'Attendee collection',
    description: 'The delegates and sponsors, from a CSV file or HubSpot.',
    icon: UserMultiple02Icon,
  },
  {
    value: COLLECTION_KINDS.sessions,
    title: 'Event collection',
    description: 'The programme: sessions with their day, times, location and speakers.',
    icon: Calendar03Icon,
  },
];

export const getCollectionKind = collection =>
  collection?.collectionData?.collectionType || COLLECTION_KINDS.knowledgeBase;

export const isRecordCollection = collection =>
  [COLLECTION_KINDS.attendees, COLLECTION_KINDS.sessions].includes(getCollectionKind(collection));

export const getCollectionKindOption = collectionOrKind => {
  const kind =
    typeof collectionOrKind === 'string' ? collectionOrKind : getCollectionKind(collectionOrKind);
  return (
    COLLECTION_KIND_OPTIONS.find(option => option.value === kind) || COLLECTION_KIND_OPTIONS[0]
  );
};

/** Attendee type values: anything that reads sponsor, exhibitor or partner is a sponsor. */
export const ATTENDEE_TYPES = [
  { value: 'delegate', label: 'Delegate' },
  { value: 'sponsor', label: 'Sponsor' },
];
export const toAttendeeType = (value, attendeeTypeValues) => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (attendeeTypeValues?.[text]) return attendeeTypeValues[text];
  return /sponsor|exhibitor|partner/i.test(text) ? 'sponsor' : 'delegate';
};

/**
 * Fields of an attendee collection. `required` fields are the base every row needs to be matched;
 * the rest are mapped when the source has them. `match` finds the field from a source column's name
 * (the dictionary match; "Auto-map with AI" asks the matching service for the rest).
 */
export const ATTENDEE_FIELDS = [
  {
    key: 'firstName',
    label: 'First name',
    required: true,
    match: /^(first ?name|firstname|given)/i,
  },
  {
    key: 'lastName',
    label: 'Last name',
    required: true,
    match: /^(last ?name|lastname|surname|family)/i,
  },
  { key: 'email', label: 'Email', required: true, type: 'email', match: /e-?mail/i },
  { key: 'jobTitle', label: 'Job title', required: true, match: /job|title|position|role$/i },
  {
    key: 'company',
    label: 'Company',
    required: true,
    match: /^(company|company name|organi[sz]ation|firm|employer)$/i,
  },
  {
    key: 'attendeeType',
    label: 'Attendee type',
    required: true,
    type: 'attendeeType',
    hint: 'Sponsor, exhibitor or partner come in as sponsors; everyone else as a delegate.',
    match: /attendee|ticket|type|buyer|seller|badge/i,
  },
  {
    key: 'crmId',
    label: 'CRM contact ID',
    required: true,
    hint: 'The contact’s id in your CRM. It keeps one record per person: a row with an id used twice in the file is left out; a row without one is kept and flagged Incomplete.',
    match: /crm|uid|record id|object id|contact id|hs_object_id|^id$/i,
  },
  {
    key: 'mobile',
    label: 'Mobile phone',
    type: 'phone',
    hint: 'For WhatsApp messages.',
    match: /phone|mobile|cell/i,
  },
  {
    key: 'stand',
    label: 'Stand',
    hint: 'Sponsors only: where their meetings happen.',
    sponsorOnly: true,
    match: /stand|booth/i,
  },
  { key: 'industry', label: 'Industry', match: /industry|sector/i },
  { key: 'companySize', label: 'Company size', match: /size|employees|headcount/i },
  { key: 'budget', label: 'Budget', match: /budget|spend/i },
  { key: 'priority', label: 'Priority (1–5)', type: 'number', match: /priority/i },
  {
    key: 'wants',
    label: 'Wants',
    type: 'longText',
    hint: 'Delegates: what they want from the event and who they hope to meet.',
    delegateOnly: true,
    match: /want|objective|goal|interest(ed)? in$/i,
  },
  {
    key: 'notInterestedIn',
    label: 'Not interested in',
    type: 'longText',
    hint: 'Delegates: topics and companies to leave out.',
    delegateOnly: true,
    match: /not interested|avoid|exclude/i,
  },
  {
    key: 'lookingFor',
    label: 'Looking for',
    type: 'longText',
    hint: 'Sponsors: who they want to meet and what they offer.',
    sponsorOnly: true,
    match: /looking for|offering|offer|target/i,
  },
];

/** Fields of an event collection: one row per session. */
export const SESSION_FIELDS = [
  { key: 'title', label: 'Session title', required: true, match: /title|^session$|^name$/i },
  { key: 'day', label: 'Day', required: true, type: 'date', match: /date|day/i },
  {
    key: 'startTime',
    label: 'Start time',
    required: true,
    type: 'time',
    match: /start|^time|from/i,
  },
  {
    key: 'endTime',
    label: 'End time',
    required: true,
    type: 'time',
    match: /end|until|finish|to$/i,
  },
  {
    key: 'location',
    label: 'Location',
    required: true,
    match: /location|venue|room|stage|hall/i,
  },
  {
    key: 'format',
    label: 'Format',
    hint: 'Keynote, seminar, workshop, roundtable or networking.',
    match: /format|category|kind/i,
  },
  { key: 'track', label: 'Track', match: /track|theme|stream|theatre/i },
  {
    key: 'hostCompany',
    label: 'Host company',
    hint: 'The sponsor hosting it: everyone who saves it becomes their lead.',
    match: /host(ed)?[ _-]?(by|company|sponsor)|sponsor/i,
  },
  {
    key: 'speakers',
    label: 'Speakers',
    hint: 'Separate names with commas.',
    match: /speaker|presenter|host/i,
  },
  { key: 'capacity', label: 'Capacity', type: 'number', match: /capacity|seats|max/i },
  {
    key: 'inviteOnly',
    label: 'Invite only',
    type: 'boolean',
    hint: 'Yes for sessions only invited people can join.',
    match: /invite|private|closed|access/i,
  },
  {
    key: 'description',
    label: 'Description',
    type: 'longText',
    match: /description|overview|summary|about/i,
  },
];

export const getCollectionFields = collectionOrKind => {
  const kind =
    typeof collectionOrKind === 'string' ? collectionOrKind : getCollectionKind(collectionOrKind);
  if (kind === COLLECTION_KINDS.attendees) return ATTENDEE_FIELDS;
  if (kind === COLLECTION_KINDS.sessions) return SESSION_FIELDS;
  return [];
};

/** What a collection's rows are called, for counts and buttons. */
export const getRecordNoun = (collectionOrKind, count = 2) => {
  const kind =
    typeof collectionOrKind === 'string' ? collectionOrKind : getCollectionKind(collectionOrKind);
  if (kind === COLLECTION_KINDS.attendees) return count === 1 ? 'attendee' : 'attendees';
  if (kind === COLLECTION_KINDS.sessions) return count === 1 ? 'session' : 'sessions';
  return count === 1 ? 'item' : 'items';
};

/** Required fields a row is missing (stand is never required). */
export const getMissingFields = (fields, values = {}) =>
  fields
    .filter(field => field.required)
    .filter(field => String(values?.[field.key] ?? '').trim() === '')
    .map(field => field.key);

/**
 * The mapping a collection saves (`collectionData.fieldMapping`, and `savedMapping` on an import
 * preview): `{ source: 'csv' | 'hubspot', columns: { [sourceColumn]: field key | 'skip' },
 * attendeeTypeValues?: { [sourceValue]: 'delegate' | 'sponsor' }, savedAt }`.
 * The Map fields step keeps `{ [sourceColumn]: field key | '' }`, where '' is "Don't import".
 */
export const FIELD_MAPPING_SKIP = 'skip';
/** An attendee column kept on the row as it is, under its own name, for cohorts (e.g. hosted_buyer). */
export const FIELD_MAPPING_KEEP = 'keep';

/** The step's mapping as the API takes it on import. */
export const toFieldMapping = (source, mapping = {}, attendeeTypeValues) => ({
  source,
  columns: Object.fromEntries(
    Object.entries(mapping).map(([column, field]) => [column, field || FIELD_MAPPING_SKIP])
  ),
  ...(attendeeTypeValues && Object.keys(attendeeTypeValues).length ? { attendeeTypeValues } : {}),
});

/** A name as the exact-name match compares it: no trailing note in brackets, case or spaces. */
const plainName = text =>
  String(text ?? '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/\s+/g, '')
    .toLowerCase();

/**
 * The exact-name match: each column (not in `taken`'s columns) to the field whose label or key is
 * its name or label ("First name", "firstname", "Priority (1–5)"). A field in `taken` or already
 * given to an earlier column isn't used twice. Returns `{ [column]: field key }` for the matches only.
 */
export const matchColumnsByName = (fields = [], columns = [], taken = new Set()) => {
  const used = new Set(taken);
  const mapping = {};
  columns.forEach(column => {
    const name = typeof column === 'string' ? column : column?.name;
    if (!name) return;
    const names = [name, typeof column === 'string' ? null : column?.label]
      .filter(Boolean)
      .map(plainName)
      .filter(Boolean);
    const field = fields.find(
      item =>
        !used.has(item.key) &&
        (names.includes(plainName(item.label)) || names.includes(plainName(item.key)))
    );
    if (!field) return;
    mapping[name] = field.key;
    used.add(field.key);
  });
  return mapping;
};

/**
 * Where Map fields starts: the saved mapping for every column that still exists, then the
 * suggestion for the columns left. Returns `{ mapping, fromSaved }` (the columns the saved mapping filled).
 */
export const startingFieldMapping = (columns = [], savedMapping, suggestedMapping = {}) => {
  const saved = savedMapping?.columns || {};
  const fromSaved = [];
  const mapping = {};
  columns.forEach(column => {
    const name = typeof column === 'string' ? column : column?.name;
    if (!name) return;
    if (Object.prototype.hasOwnProperty.call(saved, name)) {
      mapping[name] = saved[name] === FIELD_MAPPING_SKIP ? '' : saved[name];
      fromSaved.push(name);
    } else {
      mapping[name] = suggestedMapping?.[name] || '';
    }
  });
  return { mapping, fromSaved };
};

/**
 * A field's label inside a sentence ("1 missing first name"): its first letter lowercased,
 * unless the first word is an abbreviation, so "CRM contact ID" keeps its capitals.
 */
export const fieldLabelInSentence = label => {
  const value = String(label ?? '');
  const firstWord = value.split(/\s/)[0];
  if (firstWord.length > 1 && firstWord === firstWord.toUpperCase()) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
};
